import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { DocumentRecord } from '@/types/catalog'
import { apiService } from '@/service/api'
import InterviewPreview from '@/routes/IntelligentBoard/DocumentPreviewShape/InterviewPreview'
import DatasheetPreview from '@/components/DatasheetPreview'
import TimeseriesPreview from '@/components/TimeseriesPreview'
import DocumentFilePreview from '@/routes/IntelligentBoard/DocumentPreviewShape/PdfPreview'
import EditorWrapper from '@/routes/GraphEditor/EditorWrapper'
import { FileText, Loader2, Image } from 'lucide-react'
import VisualElementsPanel from '@/components/VisualElementsPanel'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'
import UserArtifactPreview from '@/components/UserArtifact/UserArtifactPreview'
import VideoBriefPreview from '@/components/VideoBriefPreview'

type DocumentPreviewModalProps = {
  isOpen: boolean
  onClose: () => void
  document: DocumentRecord | null
}

const DocumentPreviewModal = ({
  isOpen,
  onClose,
  document,
}: DocumentPreviewModalProps) => {
  const { t } = useTranslation(['data_catalog', 'board'])
  const [loading, setLoading] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const [showVisualElements, setShowVisualElements] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pnidDocument, setPnidDocument] = useState<DocumentRecord | null>(null)
  // Optimistic rename overlay (the document is owned by the parent list).
  const [nameOverride, setNameOverride] = useState<string | null>(null)

  // Fetch PNID document for graph documents to get the original filename
  useEffect(() => {
    const fetchPnidDocument = async () => {
      if (!document || document.type !== 'graph' || !document.properties.pnid) {
        setPnidDocument(null)
        return
      }

      setLoading(true)
      try {
        const pnidDoc = await apiService.getDocument(document.properties.pnid)
        setPnidDocument(pnidDoc)
      } catch (err) {
        console.error('Failed to load PNID document:', err)
      } finally {
        setLoading(false)
      }
    }

    if (isOpen && document) {
      fetchPnidDocument()
    }
  }, [document, isOpen])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowTranscript(false)
      setShowVisualElements(false)
      setCurrentPage(1)
      setPnidDocument(null)
      setNameOverride(null)
    }
  }, [isOpen])

  // Clear the optimistic rename overlay when the previewed document changes, so
  // a name typed for one doc can't leak onto the next one shown in the modal.
  useEffect(() => {
    setNameOverride(null)
  }, [document?.id])

  const previewContent = useMemo(() => {
    if (!document) return null
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500 dark:text-dark-text-muted" />
        </div>
      )
    }

    switch (document.type) {
      case 'graph':
        return (
          <EditorWrapper key={document.id} graphId={document.id} initialZoom={1} isReadOnly />
        )
      case 'interview':
        return <InterviewPreview docId={document.id} />
      case 'document': {
        // xlsx/xls/csv files with type 'document' should use the datasheet viewer
        // but only if the SQLite db has been created (db_path exists)
        const ext = document.name?.split('.').pop()?.toLowerCase()
        if (['xlsx', 'xls', 'csv'].includes(ext || '') && document.properties?.db_path) {
          return <DatasheetPreview docId={document.id} />
        }
        if (['xlsx', 'xls', 'csv'].includes(ext || '')) {
          return (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('data_catalog:file_still_processing', 'File is still being processed...')}
            </div>
          )
        }
        return (
          <DocumentFilePreview
            docId={document.id}
            showTranscript={showTranscript}
            goToPage={currentPage}
          />
        )
      }
      case 'pnid':
      case 'pfd':
        return (
          <DocumentFilePreview
            docId={document.id}
            showTranscript={showTranscript}
            goToPage={currentPage}
          />
        )
      case 'timeseries':
        // Show DatasheetPreview if db_path exists (xlsx/csv files)
        // Show TimeseriesPreview (JSON viewer) for JSON timeseries files
        if (document.properties?.db_path) {
          return <DatasheetPreview docId={document.id} />
        }
        return <TimeseriesPreview docId={document.id} />
      case 'datasheet':
        return <DatasheetPreview docId={document.id} />
      // FS-557 "User Artifact" — same viewer the board and the catalogue's own
      // open action use. Without this it fell to `default:` and reported
      // "Document not found" for a document that exists (UI-03).
      case 'transcript':
        return <UserArtifactPreview docId={document.id} />
      // A rendered "Explain as video" brief.
      case 'video':
        return <VideoBriefPreview docId={document.id} status={document.status} />
      // No viewer exists for a device config, and a type this build has not
      // heard of has none either. Both are "we cannot show this", NOT
      // "document not found" — the document is right here, it was passed in as
      // a prop. The old default said the file was missing (UI-03).
      case 'device_config':
      default:
        return (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
            {t(
              'board:document_preview.preview_not_available',
              'Preview not available for this document type'
            )}
          </div>
        )
    }
  }, [document, loading, showTranscript, currentPage, t])

  // Check if document supports transcript (PDF documents, not spreadsheets)
  const isSpreadsheet = (['xlsx', 'xls', 'csv'].includes(
    document?.name?.split('.').pop()?.toLowerCase() || ''
  ))
  const supportsTranscript =
    (document?.type === 'document' && !isSpreadsheet) || document?.type === 'pnid' || document?.type === 'pfd'

  // Check if document supports visual elements (PDF documents, not spreadsheets)
  const supportsVisualElements =
    (document?.type === 'document' && !isSpreadsheet) || document?.type === 'pnid' || document?.type === 'pfd'

  // Handle visual element click - navigate to specific page
  const handleElementClick = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  // Determine which name to display - prefer PNID filename for graph documents
  const displayName = useMemo(() => {
    if (nameOverride !== null) return nameOverride
    if (pnidDocument) {
      return pnidDocument.name
    }
    return document?.name || ''
  }, [document, pnidDocument, nameOverride])

  // P&ID/PFD graph docs display their SOURCE document's name, so renaming
  // targets that source (mirrors the board card / preview-header handlers).
  const renameDocument = useRenameDocument()
  const handleRename = (newName: string) => {
    if (!document) return
    const targetId =
      (document.type === 'graph' ? document.properties?.pnid : document.id) || document.id
    void renameDocument(targetId, newName, {
      previousName: displayName,
      // The board keys this doc's card overlay by its own id (the graph doc for
      // P&ID/PFD), while the API + documents store use the renamed source
      // (targetId) — mirrors DocumentPreview / the on-canvas card handler.
      overlayKey: document.id,
      applyName: setNameOverride,
    })
  }

  return (
    <Modal
      className={`w-full h-[80vh] ${
        showVisualElements ? 'max-w-7xl' : 'max-w-5xl'
      }`}
      isOpen={isOpen}
      onClose={onClose}
      title={
        displayName ? (
          <FileNameWithCopy
            name={displayName}
            className="!text-lg !font-semibold"
            onRename={document ? handleRename : undefined}
          />
        ) : undefined
      }
      footer={
        <div className="flex gap-2">
          {supportsTranscript && (
            <Button
              variant="outline"
              onClick={() => setShowTranscript((prev) => !prev)}
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              {showTranscript
                ? t('board:document_preview.hide_transcript', 'Hide Transcript')
                : t(
                    'board:document_preview.show_transcript',
                    'Show Transcript'
                  )}
            </Button>
          )}
          {supportsVisualElements && (
            <Button
              variant="outline"
              onClick={() => setShowVisualElements((prev) => !prev)}
              className="flex items-center gap-2"
            >
              <Image className="w-4 h-4" />
              {showVisualElements
                ? t(
                    'board:document_preview.hide_visuals',
                    'Hide Visual Elements'
                  )
                : t(
                    'board:document_preview.show_visuals',
                    'Show Visual Elements'
                  )}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('common:buttons.close', 'Close')}
          </Button>
        </div>
      }
    >
      <div className="h-[calc(80vh-140px)] overflow-hidden flex">
        <div className={`flex-1 min-h-0 ${showVisualElements ? 'pr-4' : ''}`}>
          {previewContent}
        </div>
        {showVisualElements && document?.id && (
          <VisualElementsPanel
            documentId={document.id}
            isVisible={showVisualElements}
            onElementClick={handleElementClick}
            onClose={() => setShowVisualElements(false)}
          />
        )}
      </div>
    </Modal>
  )
}

export default DocumentPreviewModal
